import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { Account, Feature, queries, useAuthConfig } from "@theprelude/core";

type Env = "local" | "us1" | "us2" | "eu1";

const env = import.meta.env.VITE_PRELUDE_ENVIRONMENT;

const overrides: Partial<Account["features"]> = {};

export function getFeatures(account: Account) {
  const features =
    env === "local" ? { ...account.features, ...overrides } : account.features;
  return Object.entries(features)
    .filter(([, value]) => value)
    .map(([key]) => key as Feature);
}

export function hasFeatures(account: Account, features: Feature[]) {
  const accountFeatures = new Set(getFeatures(account));

  return features.every((feature) => accountFeatures.has(feature));
}

export function useFeature(...flags: Feature[]) {
  const authConfig = useAuthConfig();
  const { data: isAuthorized } = useSuspenseQuery({
    ...queries.iam.getAccount(authConfig),
    select: (account) => hasFeatures(account, flags),
  });

  return isAuthorized;
}

export function Flag({
  envs,
  children,
  features,
  fallback,
}: {
  envs?: Env[];
  features?: Feature[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  const authConfig = useAuthConfig();
  const { data: accountFeatures } = useQuery({
    ...queries.iam.getAccount(authConfig),
    select: (account) => getFeatures(account),
  });

  const isEnv = envs?.includes(env) ?? true;
  const isFeature =
    features?.every((feature) => accountFeatures?.includes(feature)) ?? true;

  if (isEnv && isFeature) {
    return <>{children}</>;
  }

  return fallback ?? null;
}
